// array helpsrs
import moment from 'moment-timezone'
import uniqueBy from 'lodash.uniqby'
import { pointsWithinPolygon, polygon } from '@turf/turf'

/**
 *
 * @param {array} arr
 * @param {number} [chunkSize]
 * @param {array} [cache]
 * @returns
 */
export const chunk = (arr, chunkSize = 1, cache = []) => {
	const tmp = [...arr]
	if (chunkSize <= 0) return cache
	while (tmp.length) cache.push(tmp.splice(0, chunkSize))
	return cache
}

/**
 *
 * @param {array} arr
 * @param {number} rows
 */
export const chunkIntoRows = (arr, rows) => {
	const len = arr.length
	const chunkSize = len % rows
	return chunk(arr, chunkSize)
}

/**
 *
 * @param {array} a
 * @param {number} n
 * @param {boolean} balanced
 * @returns
 */
export const chunkify = (a, n, balanced) => {
	if (n < 2) return [a]

	let len = a.length,
		out = [],
		i = 0,
		size

	if (len % n === 0) {
		size = Math.floor(len / n)
		while (i < len) {
			out.push(a.slice(i, (i += size)))
		}
	} else if (balanced) {
		while (i < len) {
			size = Math.ceil((len - i) / n--)
			out.push(a.slice(i, (i += size)))
		}
	} else {
		n--
		size = Math.floor(len / n)
		if (len % size === 0) size--
		while (i < size * n) {
			out.push(a.slice(i, (i += size)))
		}
		out.push(a.slice(size * n))
	}

	return out
}

export const asyncDelay = async delayMs => {
	return new Promise(resolve => {
		setTimeout(() => {
			resolve()
		}, delayMs)
	})
}

/**
 * Check if the user agent contains the string "tesla"
 * @returns A boolean value.
 */
export const isTesla = () => {
	return /tesla/.test(navigator.userAgent.toLowerCase())
}

/**
 *
 * @param {number} ratings
 * @param {number} outOf
 */
export const sliceIt = (ratings, outOf = 5) => {
	try {
		const arr = Array.from(Array(Math.floor(ratings))).map(el => 1) // fill an array with ones based on the number of "full" stars
		const diff = ratings - Math.floor(ratings) // see what the value is for the next star (if ratings = 4.5 it will be 0.5)
		if (diff > 0) arr.push(Number(diff.toFixed(1)))
		while (arr.length < outOf) arr.push(0)
		return arr
	} catch (error) {
		return Array.from(Array(outOf)).map(el => 0)
	}
}

/**
 *
 * @param {number} ts
 * @param {{timeDiff:number, replacementText:string, prefix:string, timezone?:string, format?:string}} [options]
 * @returns {string} time string
 */
export const returnTimeString = (ts, options = {}) => {
	const { format = 'h:mm a', prefix = '' } = options
	const mom = moment(ts * 1000)
	mom.locale(navigator.language)
	if (moment.isMoment(mom)) {
		if (options.timezone) mom.tz(options.timezone)
		if (mom.diff(moment()) <= options.timeDiff)
			return options.replacementText
		return `${prefix}${mom.format(format)}`
	}
	return ''
}

export const returnTimeAgo = ts => {
	const mom = moment(ts * 1000)
	if (moment.isMoment(mom)) return mom.fromNow()
	return ''
}

/**
 *
 * @param {'Driving'|'Charging'|'Parked'} status
 * @param {{is_driving:boolean, is_charging:boolean}} booleans
 */
export const returnStatus = (status, booleans = {}) => {
	const { is_charging, is_driving } = booleans
	if (is_driving || status === 'Driving') return 'Driving'
	if (is_charging || status === 'Charging') return 'Charging'
	return status
}

export const isNullOrUndefined = thing => thing === null || thing === undefined

export const capitalizeWord = word => {
	if (typeof word !== 'string') return ''
	return word
		.split('')
		.map((letter, i) => (i === 0 ? letter.toUpperCase() : letter))
		.join('')
}

/**
 *
 * @param {string} name
 * @returns
 */
export const returnInitials = name => {
	if (typeof name !== 'string') return ''
	return name
		.split(' ')
		.filter((_, i, arr) => i === 0 || i === arr.length - 1)
		.map(word => word[0]?.toUpperCase())
		.join('')
}

/**
 *
 * @param {string} json
 * @returns {object|null}
 */
export const parsePotentialJSON = json => {
	try {
		if (typeof json !== 'string') throw new Error('not a string')
		return JSON.parse(json)
	} catch (error) {
		return null
	}
}

export const formatNumber = num => {
	try {
		return new Intl.NumberFormat(navigator.language).format(num)
	} catch (error) {
		return num
	}
}

export const formatCurrency = (num, currency) => {
	try {
		return new Intl.NumberFormat(navigator.language, { style: 'currency', currency }).format(num)
	} catch (error) {
		return num
	}
}

/**
 * 
 * @param {string} string 
 */
export const formatTime = string => {
	try {
		const [h, m] = string.split(':')
		const date = new Date()
		date.setHours(h)
		date.setMinutes(m)
		return new Intl.DateTimeFormat(navigator.language, {
			timeStyle: 'short',
		}).format(date)
	} catch (error) {
		return string
	}
}

/**
 *
 * @param {any[]} oldArray
 * @param {any[]} newArray
 * @returns
 */
export const returnUniqueChargers = (oldArray, newArray) => {
	const merged = [...oldArray, ...newArray]
	return uniqueBy(merged, 'id')
}

/**
 * 
 * @param {import('@turf/turf').FeatureCollection} source 
 * @param {{ne:[lng:number,lat:number],sw:[lng:number,lat:number]}} bounds 
 */
export const returnPinsWithinViewport = (source, bounds) => {
    if (!bounds || !source) return []
    try {
        const { ne, sw } = bounds
        const nw = [ne[0],sw[1]]
        const se = [sw[0],ne[1]]
        const searchWithin = polygon([[nw, ne, se, sw, nw]], { name: 'poly' })
        const within = pointsWithinPolygon(source, searchWithin)
        return within.features.map(({ properties }) => properties)
    } catch (error) {
        console.log('returnPins error', { error })
        return []    
    }
}